import { Button, Input, Select, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@ant-design/pro-components';

import { applicationStateReset } from '../../../features/application/applicationSlice';
import { numberWithCommas } from '../../../helpers/number';
import { getSubmitForApprovalIslamicApplicationsThunk } from '../../../services/islamicApplicationService/islamicApplicationThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDate, displayDateAndTime } from '../../../utils/datetime.util';

import type { IslamicApplication } from '../../../features/islamicApplication/islamicApplicationSlice';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { getAdminsThunk } from '../../../services/adminService/adminThunk';

const { Search } = Input;

const IslamicApprovalReviewPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { submitForApprovalIslamicApplicationList } = useSelector(
    (state: RootState) => state.islamicApplication
  );
  const { admins } = useSelector((state: RootState) => state.admin);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {},
      filters: {},
    }),
    []
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<IslamicApplication>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('applicationNo');
  const [searchValue, setSearchValue] = useState('');

  const tablePagination = {
    total: submitForApprovalIslamicApplicationList?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Application Date',
      dataIndex: 'submissionDate',
      key: 'submissionDate',
      sorter: (a, b) => a.submissionDate - b.submissionDate,
      sortOrder: sortedInfo.columnKey === 'submissionDate' ? sortedInfo.order : null,
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Submission Date & Time',
      dataIndex: 'verifierSubmitDate',
      key: 'verifierSubmitDate',
      width: 120,
      render: (text) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Application No.',
      dataIndex: 'applicationNo',
      key: 'applicationNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/islamic/application/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => record?.applicantFirstName ?? '-',
    },
    {
      title: 'Applicant NRIC',
      render: (_, record) => record?.applicantNric ?? '-',
    },
    {
      title: 'Applied Loan Amount',
      key: 'loanApplied',
      dataIndex: 'loanApplied',
      render: (text) => (text ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Finance Amount',
      key: 'financeAmount',
      dataIndex: 'financeAmount',
      render: (text) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Credit Bucket',
      dataIndex: 'bucket',
      key: 'bucket',
    },
    ...(process.env.REACT_APP_CASE_ASSIGNMENT_ENABLED === 'YES'
      ? [
          {
            title: 'Case Assignment',
            key: 'caseAssignment',
            children: [
              {
                title: 'Bucket',
                dataIndex: ['caseAssignment', 'bucket', 'bucketName'],
                key: 'caseAssignmentBucketName',
                render: (bucketName: any) => {
                  return bucketName ?? '-';
                },
              },
              {
                title: 'Group',
                dataIndex: ['caseAssignment', 'group', 'groupCode'],
                key: 'caseAssignmentBucketName',
                render: (groupCode: any) => {
                  return groupCode ?? '-';
                },
              },
              {
                title: 'User',
                dataIndex: ['caseAssignment', 'user', 'userId'],
                key: 'caseAssignmentUserId',
                render: (adminId: string) => {
                  const admin = admins.data.find((admin) => admin.id === adminId);
                  return admin?.username ?? '-';
                },
              },
            ],
          },
        ]
      : []),
  ];

  const fetchApplications = async (params?: any) => {
    await dispatch(getSubmitForApprovalIslamicApplicationsThunk(params));
  };

  const fetchAdmins = async (params?: any) => {
    await dispatch(getAdminsThunk({ params }));
  };

  const resetApplicationState = async () => {
    await dispatch(applicationStateReset());
  };

  const onSearch = (value: string) => {
    value = value.trim();
    if (value) {
      fetchApplications({
        ...initialParams,
        filters: { ...filterState },
        search: { [searchFiled]: value.toUpperCase() },
      });
    } else {
      fetchApplications({
        ...initialParams,
        filters: { ...filterState },
      });
    }
    setFilterState({ ...initialParams.filters });
  };

  const handleTableChange: TableProps<IslamicApplication>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      searchValue.length === 0
        ? {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
            search: {
              [searchFiled]: searchValue.toUpperCase(),
            },
          };

    fetchApplications(param);
    setSortedInfo(sorter as SorterResult<IslamicApplication>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 20,
    });
  };

  useEffect(() => {
    resetApplicationState();
    fetchApplications(initialParams);
    fetchAdmins({
      pagination: {
        current: 1,
        pageSize: 10000, // get all admins
      },
    });
  }, [initialParams]);

  return (
    <>
      <Helmet>
        <title>Approval Review - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Approval Review" />
      <Input.Group compact>
        <Select
          defaultValue="applicationNo"
          style={{ width: 200 }}
          onChange={(value) => setSearchFiled(value)}
          options={[
            { value: 'applicationNo', label: 'Application No' },
            { value: 'applicantFirstName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' },
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={submitForApprovalIslamicApplicationList?.data?.data}
        loading={submitForApprovalIslamicApplicationList?.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default IslamicApprovalReviewPage;
